import { lazy, useEffect } from 'react';

import { LoginCallback } from '@okta/okta-react';
import { Route, Redirect, Switch, useLocation } from 'react-router-dom';

import CreditLiftView from '@@/containers/CreditLiftView';
import QAStartScreensContainer from '@@/containers/QAStartScreensContainer';
import { useConfig } from '@@/services/useConfig';
import { useFeatureFlags } from '@@/services/useFeatureFlags';

import AboutUsView from '../AboutUsView';
import IneligibleView from '../IneligibleView';
import LandingHome from '../LandingHome';
import LandingReferral from '../LandingReferral';
import NotFoundView from '../NotFoundView';
import PrivacyPolicyView from '../PrivacyPolicyView';
import ProtectedRoute from '../ProtectedRoute';
import TermsView from '../TermsView';

const AutoContainer = lazy(() => import('@@/containers/AutoContainer'));
const RentersSignUpView = lazy(() => import('@@/containers/RentersSignUpView'));
const ConfirmationView = lazy(() => import('@@/containers/ConfirmationView'));
const VerifyEmailView = lazy(() => import('@@/containers/VerifyEmailView'));
const AutoMFAEnrollContainer = lazy(
  () => import('@@/containers/AutoMFAEnrollContainer')
);
const CreditReorderContainer = lazy(
  () => import('@@/containers/CreditReorderContainer')
);
const LoginView = lazy(() => import('@@/containers/LoginView'));
const LogoutView = lazy(() => import('@@/containers/LogoutView'));
const MyAccountContainer = lazy(
  () => import('@@/containers/MyAccountContainer')
);
const PlanContainer = lazy(() => import('@@/containers/PlanContainer'));
const PolicyContainer = lazy(() => import('@@/containers/PolicyContainer'));
const isDevOrQAEnvironment =
  process.env.REACT_APP_ENV === 'development' ||
  process.env.REACT_APP_ENV === 'qa' ||
  process.env.REACT_APP_ENV === 'test';

// Routes are defined separately from App.tsx so that anything within this file,
// or elsewhere in the component tree has access to all provider contexts.
export default function Routes() {
  const tenant = process.env.REACT_APP_TENANT || '';
  const { isFeatureEnabled, tenantFlags } = useFeatureFlags();
  const isQAScreenActive =
    isDevOrQAEnvironment || isFeatureEnabled(`${tenant}_qa_start_screen`);
  const {
    isAccountManagementEnabled,
    isAutoPurchaseFlowEnabled,
    isRentersQuoteCreationEnabled,
    isSelfServiceEnabled,
    isPrivacyAndTermsEnabled,
  } = tenantFlags;
  const { config } = useConfig();
  const {
    redirectUrls: { indexRedirectUrl },
  } = config;

  const location = useLocation();

  useEffect(
    function setupPageAnalyticsAndZendesk() {
      window?.zE?.('webWidget', 'updatePath');
    },
    [location.pathname]
  );

  return (
    <Switch>
      {isAutoPurchaseFlowEnabled && (
        <Route
          exact
          path="/"
          render={() => {
            if (indexRedirectUrl) {
              window.location.href = indexRedirectUrl;
              return null;
            }
            return <LandingHome />;
          }}
        />
      )}
      {isSelfServiceEnabled && [
        isAccountManagementEnabled && [
          <Route key="login" exact path="/login" component={LoginView} />,
          <Route key="logout" exact path="/logout" component={LogoutView} />,
          <Route
            path="/verify-email"
            component={VerifyEmailView}
            key="verify-email"
          />,
        ],
      ]}
      {isAutoPurchaseFlowEnabled && [
        <Route key="auto" path="/auto" component={AutoContainer} />,
        <Route
          key="mfa"
          exact
          path="/mfa"
          component={AutoMFAEnrollContainer}
        />,
      ]}
      {isAutoPurchaseFlowEnabled &&
        isRentersQuoteCreationEnabled && [
          <Route
            key="signUp"
            exact
            path="/sign-up"
            component={RentersSignUpView}
          />,
          <Route key="plans" path="/start" component={PlanContainer} />,
          <Route
            key="confirmation"
            exact
            path="/confirmation"
            component={ConfirmationView}
          />,
          <Route
            key="ineligible"
            path="/ineligible"
            component={IneligibleView}
          />,
        ]}
      {isSelfServiceEnabled && [
        <ProtectedRoute
          key="myaccount"
          path={['/my-account', '/my-profile', '/my-activity']}
          component={MyAccountContainer}
        />,
        <Route
          key="ckoffer"
          exact
          path="/policies/:policyId/offer-ck"
          render={() =>
            isFeatureEnabled('m107416') ? (
              <ProtectedRoute component={CreditReorderContainer} />
            ) : (
              <NotFoundView />
            )
          }
        />,
        <ProtectedRoute
          key="policy"
          path="/policies/:policyId"
          component={PolicyContainer}
        />,
        <Route
          key="referral"
          exact
          path="/referral"
          component={LandingReferral}
        />,
        <Route key="about" path="/about-us" component={AboutUsView} />,
        <Route
          key="creditlift"
          path="/credit-lift"
          component={CreditLiftView}
        />,
      ]}
      {isPrivacyAndTermsEnabled && [
        <Route key="privacy" path="/privacy" component={PrivacyPolicyView} />,
        <Route key="terms" path="/terms" component={TermsView} />,
      ]}
      {/* TODO integration test in separate ticket ch141861 */}
      {isQAScreenActive && (
        <Route path="/_/" component={QAStartScreensContainer} />
      )}
      <Route path="/implicit/callback" component={LoginCallback} />
      <Route component={NotFoundView} />
      <Redirect
        {...(isAutoPurchaseFlowEnabled ? { to: '/' } : { to: '/my-account' })}
      />
    </Switch>
  );
}
